/**
 * Cart + Checkout + Order Confirmation.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { ChevronLeft, ShoppingBag, X, Check } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { payForMarketplace } from "../../services/razorpay";
import { toast } from "sonner";

export function Cart() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState(null);
  const [useFP, setUseFP] = useState(false);

  const refresh = () => apiClient.get("/marketplace/cart").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { if (user) refresh(); }, [user]);

  if (!user) return <div className="p-10 text-center text-ink-soft">Please log in to see your cart.</div>;
  if (!data) return <div className="p-10 text-center text-ink-soft">Loading…</div>;

  const remove = async (pid) => {
    await apiClient.post("/marketplace/cart/remove", { product_id: pid });
    refresh();
  };

  const discount = useFP && data.fitpoints_eligible ? data.fitpoints_discount_inr : 0;
  const finalTotal = Math.max(0, data.total - discount);

  return (
    <div className="app-page min-h-screen pb-24" data-testid="cart-page" style={{ background: "#F4F3F1" }}>
      <header className="sticky top-0 z-20 bg-canvas/85 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="cart-back" className="w-10 h-10 grid place-items-center"><ChevronLeft size={22} /></button>
          <h1 className="font-display text-[20px]">My Cart · {data.items.length}</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4">
        {data.items.length === 0 ? (
          <div className="text-center py-16" data-testid="cart-empty">
            <ShoppingBag size={40} className="text-ink-tertiary mx-auto" />
            <div className="font-barlow-cond text-[24px] font-bold mt-3">Your cart is empty</div>
            <div className="text-[14px] text-ink-soft mt-1 mb-4">Find something you need</div>
            <button onClick={() => navigate("/marketplace")} data-testid="browse-shop" className="px-5 h-11 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Browse Shop</button>
          </div>
        ) : (
          <>
            <div className="bg-white border border-line2 rounded-[14px] divide-y divide-line2 overflow-hidden mb-4" data-testid="cart-items">
              {data.items.map((c) => (
                <div key={c.cart_id} className="p-3 flex gap-3" data-testid={`cart-row-${c.product_id}`}>
                  <img src={(c.product.photos || [])[0]} alt="" className="w-16 h-16 rounded-[8px] object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[14px] line-clamp-2">{c.product.title}</div>
                    <div className="text-[12px] text-ink-tertiary">{c.product.seller_name}</div>
                    <div className="font-barlow-cond font-extrabold text-[18px] mt-0.5">₹{c.product.price_inr.toLocaleString("en-IN")}</div>
                  </div>
                  <button onClick={() => remove(c.product_id)} data-testid={`cart-remove-${c.product_id}`} aria-label="Remove" className="w-9 h-9 grid place-items-center text-ink-tertiary"><X size={16} /></button>
                </div>
              ))}
            </div>

            <div className="bg-white border border-line2 rounded-[16px] p-4" data-testid="order-summary">
              <Row label="Subtotal" value={`₹${data.subtotal.toLocaleString("en-IN")}`} />
              <Row label="Delivery" value={data.delivery ? `₹${data.delivery.toLocaleString("en-IN")}` : "Free"} />
              {discount > 0 && <Row label="FitPoints discount" value={`-₹${discount}`} positive />}
              <div className="h-px bg-line2 my-2" />
              <Row label="Total" value={`₹${finalTotal.toLocaleString("en-IN")}`} bold />
            </div>

            {data.fitpoints_eligible && (
              <button onClick={() => setUseFP((v) => !v)} data-testid="toggle-fitpoints" className="mt-3 w-full bg-white border border-line2 rounded-[12px] p-3 flex items-center justify-between">
                <div className="text-left">
                  <div className="text-[13.5px] font-semibold">Use 500 FitPoints for ₹50 off</div>
                  <div className="text-[11.5px] text-ink-tertiary">{useFP ? `${data.fitpoints_balance - 500} FitPoints remaining after purchase` : `You have ${data.fitpoints_balance} FitPoints`}</div>
                </div>
                <span className="w-10 h-6 rounded-full relative" style={{ background: useFP ? "#00D4FF" : "#C7C4BF" }}>
                  <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform" style={{ left: 2, transform: useFP ? "translateX(16px)" : "none" }} />
                </span>
              </button>
            )}
          </>
        )}
      </main>

      {data.items.length > 0 && (
        <div className="fixed left-0 right-0 bottom-0 z-30" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(20px)", borderTop: "1px solid #E2E0DC", padding: "12px 16px", paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
          <button
            onClick={() => navigate(`/marketplace/checkout?fp=${useFP ? 1 : 0}`)}
            data-testid="proceed-checkout"
            className="w-full h-13 rounded-pill font-bold"
            style={{ background: "#00D4FF", color: "#000", borderRadius: 100, height: 52 }}
          >
            Proceed to Checkout · ₹{finalTotal.toLocaleString("en-IN")}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bold, positive }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-[14px]" style={{ fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span className={bold ? "font-barlow-cond font-extrabold text-[18px]" : "text-[14px]"} style={{ color: positive ? "#00A067" : "#1A1A1A" }}>{value}</span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Checkout
// ──────────────────────────────────────────────────────────────
export function Checkout() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [searchParams] = useSearchParams();
  const useFP = searchParams.get("fp") === "1";
  const [cart, setCart] = useState(null);
  const [deliveryType, setDeliveryType] = useState("delivery");
  const [payMethod, setPayMethod] = useState("upi");
  const [busy, setBusy] = useState(false);
  const [addr, setAddr] = useState({
    full_name: user?.name || "",
    phone: user?.phone || "",
    address_line: "",
    city: user?.city || "",
    pincode: "",
  });

  useEffect(() => {
    apiClient.get("/marketplace/cart").then((r) => setCart(r.data)).catch(() => setCart(null));
  }, []);

  if (!cart) return <div className="p-10 text-center text-ink-soft">Loading…</div>;

  const discount = useFP && cart.fitpoints_eligible ? cart.fitpoints_discount_inr : 0;
  const total = Math.max(0, cart.total - discount);
  const canPay = addr.full_name.trim() && addr.phone.trim() && addr.address_line.trim() && addr.city.trim() && addr.pincode.trim().length >= 5;

  const pay = async () => {
    if (!canPay || busy) return;
    setBusy(true);
    try {
      const res = await payForMarketplace({
        use_fitpoints: useFP,
        delivery_type: deliveryType,
        delivery_address: addr,
      });
      navigate(`/marketplace/order-confirmed/${res.fitfrik_order_id}`, { replace: true });
    } catch (e) {
      if (e.message !== "dismissed") toast.error(e.message || "Payment failed");
      setBusy(false);
    }
  };

  return (
    <div className="app-page min-h-screen pb-28" data-testid="checkout-page" style={{ background: "#F4F3F1" }}>
      <header className="sticky top-0 z-20 bg-canvas/85 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="checkout-back" className="w-10 h-10 grid place-items-center"><ChevronLeft size={22} /></button>
          <h1 className="font-display text-[20px]">Checkout</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {/* Address */}
        <section className="bg-white border border-line2 rounded-[16px] p-4" data-testid="addr-section">
          <h2 className="font-display text-[18px] mb-2">Deliver to</h2>
          <div className="grid grid-cols-2 gap-2">
            <Input label="Full name" value={addr.full_name} onChange={(v) => setAddr({ ...addr, full_name: v })} testid="addr-name" />
            <Input label="Phone" value={addr.phone} onChange={(v) => setAddr({ ...addr, phone: v })} testid="addr-phone" />
            <div className="col-span-2"><Input label="Address line" value={addr.address_line} onChange={(v) => setAddr({ ...addr, address_line: v })} testid="addr-line" /></div>
            <Input label="City" value={addr.city} onChange={(v) => setAddr({ ...addr, city: v })} testid="addr-city" />
            <Input label="Pincode" value={addr.pincode} onChange={(v) => setAddr({ ...addr, pincode: v })} testid="addr-pincode" />
          </div>
          <div className="mt-3 flex gap-2">
            {[["delivery","Home Delivery"],["pickup","Pickup"]].map(([id,l]) => {
              const on = deliveryType === id;
              return <button key={id} onClick={() => setDeliveryType(id)} data-testid={`dt-${id}`} className="flex-1 h-10 rounded-pill text-[13px]" style={{ background: on ? "#00D4FF" : "#ECEAE7", color: on ? "#0B0B0B" : "#6B6760", fontWeight: on ? 700 : 600, borderRadius: 100 }}>{l}</button>;
            })}
          </div>
        </section>

        {/* Summary */}
        <section className="bg-white border border-line2 rounded-[16px] p-4" data-testid="checkout-summary">
          <h2 className="font-display text-[18px] mb-2">Order summary</h2>
          <div className="divide-y divide-line2">
            {cart.items.map((c) => (
              <div key={c.cart_id} className="py-2 flex items-center gap-3">
                <img src={(c.product.photos || [])[0]} className="w-10 h-10 rounded-[6px] object-cover" alt="" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{c.product.title}</div>
                  <div className="text-[11px] text-ink-tertiary">Qty {c.quantity}</div>
                </div>
                <div className="font-barlow-cond font-extrabold text-[15px]">₹{(c.product.price_inr * c.quantity).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
          <div className="h-px bg-line2 my-2" />
          <div className="flex justify-between items-center">
            <span className="text-[13px] text-ink-soft">Total{discount > 0 ? ` (after ₹${discount} FitPoints)` : ""}</span>
            <span className="font-barlow-cond font-extrabold" style={{ fontSize: 20 }}>₹{total.toLocaleString("en-IN")}</span>
          </div>
        </section>

        {/* Payment */}
        <section className="bg-white border border-line2 rounded-[16px] p-4" data-testid="pay-section">
          <h2 className="font-display text-[18px] mb-2">Pay with</h2>
          {[
            { id: "upi", label: "UPI" },
            { id: "card", label: "Credit / Debit Card" },
            { id: "netbanking", label: "Net Banking" },
            { id: "emi", label: "EMI (items above ₹1,999)", disabled: total < 1999 },
          ].map((p) => (
            <label key={p.id} className={`flex items-center gap-3 px-3 h-12 rounded-[10px] ${p.disabled ? "opacity-50" : "hover:bg-canvas2"}`} data-testid={`pay-${p.id}`}>
              <input type="radio" checked={payMethod === p.id} onChange={() => setPayMethod(p.id)} disabled={p.disabled} />
              <span className="text-[14px] font-medium">{p.label}</span>
            </label>
          ))}
          <p className="text-[11px] text-ink-tertiary mt-2">Razorpay powers all payments. You'll choose your exact method inside the checkout modal.</p>
        </section>
      </main>

      <div className="fixed left-0 right-0 bottom-0 z-30" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(20px)", borderTop: "1px solid #E2E0DC", padding: "12px 16px", paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
        <button onClick={pay} disabled={!canPay || busy} data-testid="pay-btn" className="w-full rounded-pill font-bold disabled:opacity-50" style={{ background: "#00D4FF", color: "#000", borderRadius: 100, height: 52 }}>
          {busy ? "Opening Razorpay…" : `Pay ₹${total.toLocaleString("en-IN")}`}
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-ink-tertiary font-semibold mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full h-11 px-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[14px]" />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Order confirmation
// ──────────────────────────────────────────────────────────────
export function OrderConfirmed() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  useEffect(() => {
    apiClient.get(`/marketplace/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null));
  }, [id]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="order-confirmed" style={{ background: "#F4F3F1" }}>
      <div className="w-16 h-16 rounded-full grid place-items-center" style={{ border: "2px solid #1A1A1A" }}>
        <Check size={32} strokeWidth={2.4} />
      </div>
      <h1 className="font-barlow-cond font-black mt-5" style={{ fontSize: 40, color: "#1A1A1A", lineHeight: 1 }}>Order Confirmed</h1>
      <p className="text-[13px] text-ink-tertiary mt-2">Order #{id?.slice(-8)}</p>
      <p className="text-[14px] text-ink-soft mt-2">{order?.delivery_type === "pickup" ? "Pickup arranged with seller" : "Expected delivery in 3–5 business days"}</p>
      {order && (
        <div className="mt-5 bg-white border border-line2 rounded-[14px] p-3 max-w-md w-full text-left">
          {(order.items || []).map((it) => (
            <div key={it.product_id} className="flex items-center gap-3 py-1">
              <img src={it.photo} alt="" className="w-10 h-10 rounded-[6px] object-cover" />
              <div className="flex-1 text-[13px] truncate">{it.title}</div>
              <div className="font-barlow-cond font-extrabold text-[14px]">₹{it.line_price_inr.toLocaleString("en-IN")}</div>
            </div>
          ))}
          <p className="text-[11px] text-ink-tertiary mt-2 truncate">{order.delivery_address?.address_line}, {order.delivery_address?.city} {order.delivery_address?.pincode}</p>
        </div>
      )}
      <div className="mt-6 w-full max-w-md space-y-2">
        <button onClick={() => navigate("/marketplace/orders")} data-testid="track-order" className="w-full h-12 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Track Order</button>
        <button onClick={() => navigate("/marketplace")} data-testid="continue-shopping" className="w-full h-11 rounded-pill border border-line2 font-medium" style={{ borderRadius: 100 }}>Continue Shopping</button>
      </div>
    </div>
  );
}
